// eslint-disable-next-line
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
    editorHtmlFromText, textFromEditorDom, handleEnter, handleBackspaceAtListStart, handlePasteText,
    insertStyledText, toggleListForBlock, backspaceDeleteCount,
    createPendingStyleState, pendingStyleReducer, isPendingStyleArmed, effectiveStyleMarks, STYLE_MARK_KEYS,
    findLinkAncestor, snapshotForElementContents,
} from './editorDom';
import { registerNoteEditor, unregisterNoteEditor } from './noteEditorBridge';
import { requestNoteLinkEdit } from './noteLinkEditRequestBridge';
import { noteBodyColor } from '../../../utils/canvas/noteColors';
import { LIST_INDENT, BULLET_DOT_X, BULLET_DOT_RADIUS } from '../../../utils/canvas/richTextLayout';
import { isAllowedLinkUrl } from '../../../lib/noteHtml/linkSchemes';
import NoteLinkPreview from '../../NoteLinkPreview/NoteLinkPreview';
import { PenEditIcon, DeleteIcon } from '../../Icon';

import './EditableNote.scss';

const NOTE_PADDING_X = 24;
const NOTE_PADDING_Y = 20;
const NOTE_LINE_HEIGHT = 30;
const NOTE_FONT_SIZE = 21;
const NOTE_FONT_MONO_SIZE = 21;

// Mirrors drawNote's dark note body (2026-08-03 redesign): tinted fill
// (noteBodyColor), hairline border, radius 5 world units.
const NOTE_BORDER_RADIUS = 5;

// The overlay sits INSIDE the canvas-drawn note so drawNote's border stays
// visible around the editor: inset by the stroke's outer reach (1px centered
// stroke + antialiasing), and compensate the paddings by the same amount so
// the text does not move.
const NOTE_EDITOR_INSET = 2;

// The overlay stops this many world px above the note's bottom so the canvas
// overflow indicator (chevron circle at the bottom edge) stays visible while
// editing. The bottom padding collapses to 0 to compensate — the overlay's
// content box still ends exactly at drawNote's text clip boundary.
const NOTE_EDITOR_BOTTOM_REVEAL = 14;

const EXEC_STYLE_COMMAND = { bold: 'bold', italic: 'italic', underline: 'underline', strike: 'strikeThrough' };
const EXEC_LIST_COMMAND = { bullet: 'insertUnorderedList', numbered: 'insertOrderedList' };
const MARK_LIST = ['bold', 'italic', 'underline', 'strike', 'link'];

// Puts the caret at the very end of root's content — used both at mount and
// by the bridge's focus().
const placeCaretAtEnd = (root) => {
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
};

// Puts the caret at a viewport point — used at mount when the editor was
// opened by a click on already-focused note text, so the caret lands
// exactly where the user clicked instead of end-of-text (which used to
// force a second click to reposition it). The overlay renders the same
// text at the same on-screen geometry as the canvas text (parity by
// design), so a point-based caret API resolves to the right spot with no
// extra bookkeeping on our side.
//
// document.caretPositionFromPoint is the standard API; caretRangeFromPoint
// is the WebKit/Chromium legacy equivalent kept around for engines that
// haven't shipped the standard one yet. Neither exists in jsdom, and a
// point can legitimately miss the text (padding, below the last line) or
// resolve to a node outside this editor entirely — in every one of those
// cases this falls back to the exact same placeCaretAtEnd(root) behavior
// as an opening with no point at all.
const placeCaretAtPoint = (root, x, y) => {
    let node = null;
    let offset = 0;

    if (typeof document.caretPositionFromPoint === 'function') {
        const caretPosition = document.caretPositionFromPoint(x, y);
        if (caretPosition) {
            node = caretPosition.offsetNode;
            offset = caretPosition.offset;
        }
    } else if (typeof document.caretRangeFromPoint === 'function') {
        const caretRange = document.caretRangeFromPoint(x, y);
        if (caretRange) {
            node = caretRange.startContainer;
            offset = caretRange.startOffset;
        }
    }

    // Neither API available (jsdom), or the resolved node falls outside
    // this editor (the point missed the text) — end of text, same as an
    // open with no point.
    if (!node || !root.contains(node)) {
        placeCaretAtEnd(root);
        return;
    }

    const range = document.createRange();
    range.setStart(node, offset);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
};

// Caret position expressed independent of DOM node identity: which block (a
// <p> or <li>, counted in document order) and how many characters into its
// plain text. execCommand and the whole-note style/list toggles both replace
// root's entire innerHTML to canonicalize the result, which would otherwise
// destroy the live selection — save before, restore after. Block count and
// per-block text length survive both kinds of edits (only inline tags
// change), so the position stays meaningful across the swap.
//
// Both boundary points of the selection are saved/restored this way (not
// just the collapsed caret) — a non-collapsed selection that goes through a
// canonicalizeInPlace (every execStyle/execList/execLink call) must come out
// still selecting the same text, otherwise the NEXT style action sees a
// collapsed caret and falls through to the whole-note toggle instead of
// applying to the user's selection.
const pointToBlockPosition = (root, container, offset) => {
    let blockEl = container.nodeType === 3 ? container.parentNode : container;
    while (blockEl && blockEl !== root && blockEl.tagName !== 'P' && blockEl.tagName !== 'LI') blockEl = blockEl.parentNode;
    if (!blockEl || blockEl === root) return null;

    const blocks = Array.from(root.querySelectorAll('p, li'));
    const blockIndex = blocks.indexOf(blockEl);
    if (blockIndex === -1) return null;

    const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
    let textOffset = 0;
    let node = walker.nextNode();
    while (node) {
        if (node === container) {
            textOffset += offset;
            break;
        }
        textOffset += node.nodeValue.length;
        node = walker.nextNode();
    }
    return { blockIndex, textOffset };
};

const blockPositionToPoint = (root, saved) => {
    const blocks = Array.from(root.querySelectorAll('p, li'));
    const blockEl = blocks[saved.blockIndex];
    if (!blockEl) return null;

    const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
    let remaining = saved.textOffset;
    let target = { container: blockEl, offset: 0 };
    let node = walker.nextNode();
    while (node) {
        if (remaining <= node.nodeValue.length) {
            target = { container: node, offset: remaining };
            break;
        }
        remaining -= node.nodeValue.length;
        target = { container: node, offset: node.nodeValue.length };
        node = walker.nextNode();
    }
    return target;
};

const saveCaretPosition = (root) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;

    const start = pointToBlockPosition(root, range.startContainer, range.startOffset);
    if (!start) return null;
    const end = range.collapsed ? start : pointToBlockPosition(root, range.endContainer, range.endOffset);
    if (!end) return null;
    return { start, end };
};

const restoreCaretPosition = (root, saved) => {
    if (!saved) return;
    const startPoint = blockPositionToPoint(root, saved.start);
    const endPoint = blockPositionToPoint(root, saved.end);
    if (!startPoint || !endPoint) return;

    const selection = window.getSelection();
    selection.removeAllRanges();
    if (typeof selection.setBaseAndExtent === 'function') {
        selection.setBaseAndExtent(startPoint.container, startPoint.offset, endPoint.container, endPoint.offset);
    } else {
        const range = document.createRange();
        range.setStart(startPoint.container, startPoint.offset);
        range.setEnd(endPoint.container, endPoint.offset);
        selection.addRange(range);
    }
};

// The five marks present anywhere in `node`'s ancestor chain, up to root.
// Deliberately NOT document.queryCommandState — its cross-browser behavior
// around collapsed selections and mixed formatting is inconsistent, and a
// plain ancestor walk is both testable and enough for this canonical subset.
const marksAtNode = (node, root) => {
    const marks = { bold: false, italic: false, underline: false, strike: false, link: false, linkHref: null };
    let el = node.nodeType === 3 ? node.parentNode : node;
    while (el && el !== root) {
        switch (el.tagName) {
            case 'STRONG': case 'B': marks.bold = true; break;
            case 'EM': case 'I': marks.italic = true; break;
            case 'U': marks.underline = true; break;
            case 'S': case 'STRIKE': case 'DEL': marks.strike = true; break;
            case 'A':
                if (el.getAttribute('href')) {
                    marks.link = true;
                    if (marks.linkHref === null) marks.linkHref = el.getAttribute('href');
                }
                break;
            default: break;
        }
        el = el.parentNode;
    }
    return marks;
};

// Text-editing overlay for canvas sticky notes. Opaque (it covers the canvas
// text, which drawNote skips while this is mounted); geometry and typography
// mirror drawNote exactly so open/close does not shift the text.
export const EditableNote = ({
    noteX, noteY, noteWidth, noteHeight,
    scale, originXY,
    text, color,
    alignmentHorizontal, alignmentVertical,
    isFontMonospace,
    noteId,
    onTextChange,
    initialCaretPoint,
    onCaretPointConsumed,
}) => {
    const { t } = useTranslation();

    const editorRef = useRef(null);
    const highlightLayerRef = useRef(null);
    const apiRef = useRef(null);

    // Pending typing style (spec rule 5.A/B/C, see editorDom.js's pending-
    // reducer doc comment for the full model). Own ref, not React state:
    // read/written synchronously inside DOM event handlers, never drives a
    // render (menu indicators pull it on demand via getSelectionStyles()).
    const pendingRef = useRef(createPendingStyleState());

    // Self-move suppression for the selectionchange-based disarm listener
    // below: a caret move WE cause as a side effect of our own typing/
    // backspace handling (insertStyledText, or the native delete a Backspace
    // proceeds with while pending stays armed) must not be mistaken for the
    // "user moved the caret externally" signal that disarms pending. Each of
    // those call sites sets this to true right before the op; the listener
    // consumes (resets) it on the very next selectionchange instead of
    // running its normal disarm logic. One-shot per op, not a mode.
    const selfMoveRef = useRef(false);

    // Captured together by beginLinkTarget (see its own doc comment): the
    // block-position snapshot Apply/Remove actually restore the selection
    // from, and the cloned Range the visual highlight/popover anchor are
    // read from. null whenever nothing is currently captured/painted.
    const linkTargetRef = useRef(null);

    // In-editor link CLICK popover (spec 8b): plain React state, unlike
    // every other piece of imperatively-owned content in this file
    // (linkTargetRef's highlight paint, the editor's own text) — those are
    // refs/direct DOM writes specifically to avoid re-rendering the
    // contenteditable's children; THIS content (the popover itself) has
    // nothing to protect from a re-render because it renders into a
    // completely separate subtree (a portal to document.body, see the JSX
    // below) — a state update here re-renders only that portal's own
    // (React-owned) children, never touches editorRef's real DOM at all.
    // Shape: null | { href, anchorEl }. Deliberately stores the live
    // anchorEl (not a pre-computed rect/snapshot) so Edit/Remove re-derive
    // both fresh from the CURRENT DOM at the moment they're actually
    // clicked, rather than trusting a value that could go stale between
    // open and click (the note's own DOM does not move under an open
    // popover in the cases this spec covers, but re-deriving costs nothing
    // and removes the whole class of staleness bug).
    const [linkClickPopover, setLinkClickPopover] = useState(null);
    const closeLinkClickPopover = () => setLinkClickPopover(null);

    // Auto-sizes the editor to its content so it stops being a flex child
    // forced to full cross-axis height — without this, wrapperStyles'
    // alignItems (below) has no space to distribute and vertical alignment
    // is a no-op.
    const autosize = () => {
        const el = editorRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    };

    const syncFromDom = () => {
        const el = editorRef.current;
        if (!el) return;
        onTextChange(textFromEditorDom(el));
        autosize();
        // Spec 8b: "typing (any beforeinput/keydown edit)" closes the
        // in-editor link click popover. Every text-mutating path in this
        // file — native typing (handleInput), Enter, list-start Backspace,
        // armed-pending insertStyledText, paste, AND canonicalizeInPlace
        // (execStyle/execList/execLink/removeLink, including THIS popover's
        // own Edit/Remove machinery) — funnels through this one function,
        // so closing here once covers every "an edit happened" case instead
        // of duplicating the call at each call site. A no-op re-render when
        // already closed (setState(null) on already-null state is a bailout,
        // not a render) — cheap enough to run unconditionally on every
        // keystroke.
        closeLinkClickPopover();
    };

    // Re-derives root's markup from its own (possibly execCommand-mangled)
    // innerHTML back to the canonical editable subset, preserving the caret.
    const canonicalizeInPlace = () => {
        const el = editorRef.current;
        if (!el) return;
        const saved = saveCaretPosition(el);
        el.innerHTML = editorHtmlFromText(el.innerHTML);
        restoreCaretPosition(el, saved);
        syncFromDom();
    };

    const currentRange = () => {
        const el = editorRef.current;
        if (!el) return null;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;
        const range = selection.getRangeAt(0);
        return el.contains(range.commonAncestorContainer) ? range : null;
    };

    const hasSelection = () => {
        const range = currentRange();
        return !!range && !range.collapsed;
    };

    // Non-collapsed: execCommand formats the live selection, then the result
    // is re-canonicalized in place (manual QA — execCommand does not run in
    // jsdom). Collapsed (revised spec rule 5.A): no text is touched at all —
    // the click arms/toggles that mark in the pending typing style instead
    // (own state; see editorDom.js's pending-reducer doc comment). The old
    // whole-note toggle for a collapsed caret is gone from here; it now
    // lives only in ContextMenu's headless (editor-closed) fallback path.
    const execStyle = (style) => {
        const el = editorRef.current;
        if (!el) return;
        const range = currentRange();

        if (range && !range.collapsed) {
            // A selection is present, so pending (if it were somehow still
            // armed from before this selection formed) is moot — clear it
            // defensively (routed through the reducer's own 'selectionMade'
            // event, same one the selectionchange listener uses below, so
            // there is a single source of truth for what "a selection
            // appeared" means to the state machine) rather than relying
            // solely on the async selectionchange listener to have already
            // done so.
            pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
            document.execCommand('styleWithCSS', false, false);
            document.execCommand(EXEC_STYLE_COMMAND[style]);
            canonicalizeInPlace();
            return;
        }
        if (!range) return;

        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'armToggle', mark: style });
    };

    // Non-collapsed: unchanged execCommand + canonicalize path. Collapsed
    // (revised spec rule 5.E): toggles ONLY the caret's own block (paragraph
    // <-> list item of `kind`) via a surgical DOM op, not the whole note.
    // Converting/exiting a list block is a structural edit, not a typing
    // continuation, so it disarms pending same as Enter does.
    const execList = (kind) => {
        const el = editorRef.current;
        if (!el) return;
        const range = currentRange();

        if (range && !range.collapsed) {
            pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
            document.execCommand('styleWithCSS', false, false);
            document.execCommand(EXEC_LIST_COMMAND[kind]);
            canonicalizeInPlace();
            return;
        }
        if (!range) return;

        // Converting/exiting a list block is a structural edit, not a
        // typing continuation — unconditional disarm (unlike the Backspace
        // list-unwrap path below, this one has no "nothing typed yet, so
        // keep it armed" exception carved out for it). Routed through the
        // reducer (not a direct reset) so it stays the single source of
        // truth for every pending transition.
        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
        const selection = window.getSelection();
        if (toggleListForBlock(el, selection, kind)) syncFromDom();
    };

    // Links only make sense over an actual selection — collapsed is a no-op.
    // Scheme is validated first: this is a collaborative doc, so a
    // javascript:/data: href here becomes a stored payload in every
    // collaborator's contenteditable DOM, not just the author's.
    const execLink = (url) => {
        if (!isAllowedLinkUrl(url)) return;
        const range = currentRange();
        if (!range || range.collapsed) return;
        document.execCommand('styleWithCSS', false, false);
        document.execCommand('createLink', false, url);
        canonicalizeInPlace();
    };

    const removeLink = () => {
        if (!currentRange()) return;
        document.execCommand('unlink');
        canonicalizeInPlace();
    };

    // Insert-new-link flow for a COLLAPSED caret (no selected text): inserts
    // a brand-new anchor at the caret whose visible text IS the url itself
    // (user decision — "type a link with nothing selected" creates the link
    // instead of Apply being disabled). Built as a real <a> element and
    // serialized via outerHTML so the url is entity-escaped as both text and
    // attribute; execCommand('insertHTML') keeps it one undo step and fires
    // the editor's normal input pipeline, and canonicalizeInPlace after runs
    // the same subset/sanitize pass every other mutation path goes through.
    // Scheme allowlist gates first, same as execLink (collaborative doc — a
    // javascript:/data: href would be a stored payload for every collaborator).
    const insertNewLinkAtCaret = (url) => {
        if (!isAllowedLinkUrl(url)) return;
        const range = currentRange();
        if (!range || !range.collapsed) return;
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.textContent = url;
        document.execCommand('insertHTML', false, a.outerHTML);
        canonicalizeInPlace();
    };

    // Selection state expressed as block positions (see saveCaretPosition's
    // doc comment), captured once by a caller that is about to lose the live
    // DOM selection before it can act on it — the note context menu's Link
    // submenu opens by moving focus (the trigger button, then the URL text
    // input), and a contenteditable drops its selection the instant focus
    // leaves it. `collapsed` lets the caller branch (a collapsed capture
    // means insert-a-new-link rather than wrap-the-selection — see
    // resolveLinkTarget) without re-deriving block positions itself.
    // Returns null when there is no selection inside this editor.
    const getSelectionSnapshot = () => {
        const el = editorRef.current;
        if (!el) return null;
        const saved = saveCaretPosition(el);
        if (!saved) return null;
        const collapsed = saved.start.blockIndex === saved.end.blockIndex
            && saved.start.textOffset === saved.end.textOffset;
        return { ...saved, collapsed };
    };

    // execLink/removeLink, but over a previously-captured snapshot (see
    // getSelectionSnapshot) instead of the live DOM selection — for callers
    // whose selection has already died to a focus change. Focuses the editor
    // first so execCommand actually targets it, restores the snapshot's
    // range, then runs the same body execLink/removeLink use (currentRange()
    // picks up the just-restored selection). Any armed pending typing style
    // is cleared up front, the same defensive clear execStyle/execList do
    // for their own non-collapsed branch: restoring a range here is a
    // programmatic caret move over a real (non-collapsed, by construction)
    // selection, not a typing continuation, so there is nothing left for a
    // pending style to meaningfully apply to — a "clean disarm" rather than
    // routing this through the selfMoveRef latch (which exists for the
    // opposite case: self-caused moves that must NOT disarm pending).
    const applyLinkAt = (snapshot, url) => {
        if (!snapshot || snapshot.collapsed) return;
        if (!isAllowedLinkUrl(url)) return;
        const el = editorRef.current;
        if (!el) return;
        // Routed through the reducer's 'selectionMade' event (same one
        // execStyle/execList's non-collapsed branch and the selectionchange
        // listener use) rather than a bare state reset, for a single source
        // of truth on what "a selection appeared" means — this call
        // predates the pending-style rule entirely, but routing it too was
        // a trivial one-line change once the event existed.
        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
        el.focus();
        restoreCaretPosition(el, snapshot);
        execLink(url);
    };

    const removeLinkAt = (snapshot) => {
        if (!snapshot) return;
        const el = editorRef.current;
        if (!el) return;
        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
        el.focus();
        restoreCaretPosition(el, snapshot);
        removeLink();
    };

    // --- Painted link-target flow (visible highlight while the Link submenu
    // --- is open) ------------------------------------------------------------
    //
    // getSelectionSnapshot/applyLinkAt/removeLinkAt above lose the VISIBLE
    // selection the moment the submenu's own controls take focus, even
    // though the note's text is still logically remembered — the user has no
    // on-screen indication of what "Apply"/"Remove" is about to act on while
    // the URL <input> is focused. beginLinkTarget/resolveLinkTarget/
    // removeLinkTarget below replace that flow for the menu: the target
    // text is PAINTED as plain absolutely-positioned <div>s in
    // highlightLayerRef (a DOM-based "highlight layer", Miro-style) —
    // rendered as an earlier sibling of the contenteditable, so normal DOM
    // paint order alone puts it between the wrapper's own background and
    // the editor's (transparent-backed) text, no z-index needed. This
    // superseded an earlier CSS Custom Highlight API attempt (the browser-
    // native ::highlight() pseudo-element) — same "paint, don't mutate the
    // note's own markup" goal, swapped for a universally-supported DOM
    // implementation after real-world testing. getSelectionSnapshot/
    // applyLinkAt/removeLinkAt are
    // left as-is (unused by ContextMenu now, kept for any other/future
    // caller) — getSelectionSnapshot specifically is also reused directly
    // below, not just left alone.
    //
    // Fully imperative (createElement/innerHTML, not React state) —
    // matches how EVERY other piece of this component's OWN rendered
    // content (the editor's text, its autosized height) is already
    // managed, and avoids re-rendering the whole component on every
    // open/close of the Link submenu. Safety for the contenteditable's
    // imperatively-managed children (see this file's own long-standing
    // convention): highlightLayerRef's own React element ALSO carries no
    // `children` prop (see the JSX below), the exact same shape editorRef's
    // element already uses — React's reconciler only ever diffs/replaces
    // DOM children it was told about via JSX, so a parent re-render (e.g.
    // from a pan/zoom prop change) diffs this element's attributes but
    // never touches either element's real, imperatively-owned children.

    // Paints one <div class="editable-note-highlight"> per line-box rect
    // `range` covers (Range#getClientRects() — one rect per VISUAL line,
    // unlike getBoundingClientRect()'s single union box, so a multi-line
    // selection gets one highlight strip per line, not one tall box).
    // Viewport-relative client rects are converted to layer-local CSS
    // coordinates by subtracting the layer's OWN current
    // getBoundingClientRect() — since both rects are independently
    // viewport-absolute snapshots taken back-to-back, synchronously, this
    // subtraction is automatically correct regardless of ANY scroll offset
    // anywhere in the ancestor chain (including the editor's own, if its
    // content is taller than its box) and at ANY zoom/scale level (this
    // overlay scales via inline pre-scaled font-size/dimensions, not a CSS
    // transform, so getClientRects() already reports real on-screen pixels
    // with nothing further to un-scale).
    const paintLinkTargetLayer = (range) => {
        const layer = highlightLayerRef.current;
        if (!layer) return;
        layer.innerHTML = '';
        if (typeof range.getClientRects !== 'function') return;

        const layerRect = layer.getBoundingClientRect();
        Array.from(range.getClientRects()).forEach((rect) => {
            if (rect.width <= 0 || rect.height <= 0) return;
            const strip = document.createElement('div');
            strip.className = 'editable-note-highlight';
            // 1px of breathing room above and below the line box — the bare
            // rect hugs the glyphs too tightly (user-tuned).
            strip.style.top = `${rect.top - layerRect.top - 2 * scale}px`;
            strip.style.left = `${rect.left - layerRect.left}px`;
            strip.style.width = `${rect.width}px`;
            strip.style.height = `${rect.height + 4 * scale}px`;
            layer.appendChild(strip);
        });
    };

    const clearLinkTargetLayer = () => {
        const layer = highlightLayerRef.current;
        if (layer) layer.innerHTML = '';
    };

    // Captures the live selection for the Link submenu: a block-position
    // snapshot (getSelectionSnapshot, unchanged) — the source of truth
    // Apply/Remove actually restore the selection from — and a cloned Range,
    // used ONLY for the visual paint (paintLinkTargetLayer) and the popover's
    // anchor rect (getLinkTargetRect), never to derive what gets acted on.
    //
    // Three capture shapes, by what the live selection is at open:
    //   - non-collapsed selection with VISIBLE text: the original flow below
    //     — Apply links the selected text.
    //   - collapsed caret INSIDE an existing link: redirected to
    //     beginLinkTargetForAnchor over that whole <a> (the same capture the
    //     in-editor click popover's Edit uses) — Apply re-hrefs the whole
    //     link, Remove unlinks it.
    //   - collapsed caret in plain text, OR a whitespace-only selection
    //     (spaces, empty rows — createLink over pure whitespace makes an
    //     invisible anchor that canonicalization strips right back out):
    //     captured AS a collapsed snapshot at the caret / selection START —
    //     nothing to paint, popover anchors at the caret, and Apply runs the
    //     insert-new-link flow (resolveLinkTarget branches on
    //     snapshot.collapsed) with the url itself as the link text.
    // Returns false (nothing captured/painted) only when there is no caret
    // in this editor at all.
    const beginLinkTarget = () => {
        const el = editorRef.current;
        if (!el) return false;
        const range = currentRange();
        if (!range) return false;
        if (range.collapsed || range.toString().trim() === '') {
            if (range.collapsed) {
                const anchor = findLinkAncestor(range.startContainer, el);
                if (anchor) return beginLinkTargetForAnchor(anchor);
            }
            const saved = getSelectionSnapshot();
            if (!saved) return false;
            // Collapse to the START (identity for an already-collapsed caret)
            // — both the snapshot Apply restores from and the cloned Range
            // the popover anchors to.
            const caretSnapshot = { ...saved, end: saved.start, collapsed: true };
            pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
            const caretRange = range.cloneRange();
            caretRange.collapse(true);
            linkTargetRef.current = { snapshot: caretSnapshot, range: caretRange };
            // A caret paints no strips — just make sure no stale ones linger.
            clearLinkTargetLayer();
            return true;
        }
        const snapshot = getSelectionSnapshot();
        if (!snapshot) return false;

        // Defensive clean disarm — same event/reasoning execStyle/execList's
        // own non-collapsed branch already uses. Painting the highlight
        // layer touches only its own sibling DOM subtree — never the
        // Selection API — and fires no selectionchange, so nothing about
        // opening the menu can itself disturb pending; this only guards
        // against a stale armed state from before the selection formed
        // surviving into it.
        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });

        const clonedRange = range.cloneRange();
        linkTargetRef.current = { snapshot, range: clonedRange };
        paintLinkTargetLayer(clonedRange);
        return true;
    };

    // beginLinkTarget's counterpart for the in-editor link CLICK popover
    // (spec 8b): a click on linked text places a COLLAPSED caret at the
    // click point (normal contenteditable behavior — see handleEditorClick,
    // left alone), so there is no live (non-collapsed) Selection for
    // beginLinkTarget's own currentRange() check to accept. Builds the
    // EXACT SAME capture shape (`{ snapshot, range }` in linkTargetRef)
    // directly from `anchorEl`'s full contents — via editorDom.js's
    // snapshotForElementContents for the snapshot, and
    // `Range.selectNodeContents` (the live DOM equivalent) for the cloned
    // Range — so every downstream consumer of linkTargetRef
    // (getLinkTargetRect, repaintLinkTarget, resolveLinkTarget,
    // removeLinkTarget, and NoteLinkMenu itself) needs NO changes at all to
    // serve this second capture path. `anchorEl` must be a live descendant
    // of this editor; returns false (nothing captured/painted) otherwise,
    // same contract as beginLinkTarget.
    const beginLinkTargetForAnchor = (anchorEl) => {
        const el = editorRef.current;
        if (!el || !anchorEl) return false;
        const snapshot = snapshotForElementContents(el, anchorEl);
        if (!snapshot || snapshot.collapsed) return false;

        // Same defensive clean disarm as beginLinkTarget's own — this
        // capture doesn't touch the Selection API either (no selectionchange
        // fires), so it only guards a stale armed state from surviving into
        // it.
        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });

        const range = document.createRange();
        range.selectNodeContents(anchorEl);
        linkTargetRef.current = { snapshot, range };
        paintLinkTargetLayer(range);
        return true;
    };

    // Viewport rect of the stored (cloned) Range — for the note context
    // menu's Link submenu to anchor its popover below the actual selected
    // text (the UNION of every line the selection covers, so a multi-line
    // selection anchors below its LAST line, not overlapping earlier ones —
    // see paintLinkTargetLayer's own comment on why the paint itself uses
    // per-line rects instead). getBoundingClientRect() works on a Range the
    // same as an Element in every real browser (CSSOM View, not
    // experimental) — guarded with the same typeof-function check
    // restoreCaretPosition already uses for setBaseAndExtent, because this
    // project's jsdom test environment specifically does not implement
    // Range#getBoundingClientRect (Element's is stubbed; Range's plainly is
    // not — confirmed directly). A plain object copy of the DOMRect, not
    // the DOMRect instance — cheap, and doesn't hand out a live reference.
    // `null` when nothing is captured (beginLinkTarget never ran, or
    // already resolved) OR the rect can't be computed at all.
    const getLinkTargetRect = () => {
        const stored = linkTargetRef.current;
        if (!stored) return null;
        if (typeof stored.range.getBoundingClientRect !== 'function') return null;
        const rect = stored.range.collapsed
            ? caretRectForCollapsedRange(stored.range)
            : stored.range.getBoundingClientRect();
        // All-zero / unmeasurable: return null so the caller falls back to
        // its button anchor instead of positioning a popover at the corner.
        if (!rect || (!rect.width && !rect.height && !rect.top && !rect.left)) return null;
        return {
            top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right,
            width: rect.width, height: rect.height,
        };
    };

    // Caret-position rect for a COLLAPSED Range. Browsers commonly report NO
    // client rects for a collapsed Range (the well-known caret-coordinates
    // gap: getBoundingClientRect comes back all zeros), so when the range's
    // own rect is unusable the caret is measured off the ADJACENT character
    // instead: the character AFTER the caret when there is one (caret sits on
    // that rect's LEFT edge), else the character BEFORE (RIGHT edge), else —
    // empty text node / empty block — the container element's own rect.
    // Always returns a zero-width caret-shaped rect (or null when nothing at
    // all is measurable, e.g. this project's jsdom).
    const caretRectForCollapsedRange = (range) => {
        const own = range.getBoundingClientRect();
        if (own && (own.width || own.height || own.top || own.left)) return own;

        const node = range.startContainer;
        const offset = range.startOffset;
        const caretRectFrom = (r, edge) => ({
            top: r.top, bottom: r.bottom, left: r[edge], right: r[edge], width: 0, height: r.height,
        });
        if (node.nodeType === Node.TEXT_NODE && node.length > 0) {
            const probe = document.createRange();
            if (offset < node.length) {
                probe.setStart(node, offset);
                probe.setEnd(node, offset + 1);
                const r = probe.getBoundingClientRect();
                if (r && (r.width || r.height)) return caretRectFrom(r, 'left');
            }
            if (offset > 0) {
                probe.setStart(node, offset - 1);
                probe.setEnd(node, offset);
                const r = probe.getBoundingClientRect();
                if (r && (r.width || r.height)) return caretRectFrom(r, 'right');
            }
        }
        const elNode = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
        if (elNode && typeof elNode.getBoundingClientRect === 'function') {
            const r = elNode.getBoundingClientRect();
            if (r && (r.width || r.height || r.top || r.left)) return caretRectFrom(r, 'left');
        }
        return null;
    };

    // Un-paints (clears the highlight layer's DOM children) and drops the
    // stored capture — FULL consumption. Its own function (not folded into
    // resolveLinkTarget) because both the post-restore tail of the apply/
    // remove paths below need it, unlike resolveLinkTarget(null)'s own
    // cancel branch — see that function's doc comment for why THAT path
    // deliberately does NOT call this (it needs the capture to survive).
    // Safe/idempotent: clearing an already-empty layer (or one that was
    // never painted), or a capture that's already null, is a no-op.
    const clearLinkTarget = () => {
        clearLinkTargetLayer();
        linkTargetRef.current = null;
    };

    // Re-paints the highlight layer from the STORED capture's Range —
    // for a caller (NoteLinkMenu's own mount effect, see ContextMenu.jsx)
    // whose own paint may have just been wiped by something OUTSIDE its
    // control, most notably React StrictMode's dev-only mount -> cleanup ->
    // mount double-invoke (see resolveLinkTarget's own doc comment on why
    // the cleanup's unpaint deliberately leaves the capture itself intact
    // for exactly this to consume on the second, real mount). Returns false
    // (nothing painted) when there is no capture, OR when the stored Range
    // no longer resolves inside this editor's CURRENT live DOM (e.g. the
    // note's own content was reset/torn down in between, or the Range's
    // nodes were otherwise detached) — paints nothing rather than let a
    // stale Range's getClientRects() do something undefined.
    const repaintLinkTarget = () => {
        const el = editorRef.current;
        const stored = linkTargetRef.current;
        if (!el || !stored) return false;
        const { range } = stored;
        if (!range || range.collapsed) return false;
        if (!el.contains(range.commonAncestorContainer)) return false;
        paintLinkTargetLayer(range);
        return true;
    };

    // Shared shape for resolveLinkTarget(url)/removeLinkTarget: focuses the
    // editor, restores the selection from the STORED SNAPSHOT (the source of
    // truth — see beginLinkTarget's own comment on why it's the snapshot and
    // not the Range), then hands off to `action` (execLink/removeLink) to do
    // the actual execCommand + canonicalize. A no-op when nothing is
    // captured — both callers below already need that same guard.
    const restoreLinkTargetThen = (action) => {
        const el = editorRef.current;
        if (!el) return;
        const stored = linkTargetRef.current;
        if (!stored) return;

        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
        el.focus();
        restoreCaretPosition(el, stored.snapshot);
        action();
    };

    // Resolves a beginLinkTarget() capture. `url` present: restores the
    // snapshotted selection and applies the link over it (existing execLink
    // body — scheme allowlist still gates), then FULLY consumes the capture
    // (clearLinkTarget — un-paint AND drop it). `url === null` (cancel/
    // close/outside-click/unmount/StrictMode's phantom cleanup — see
    // ContextMenu's NoteLinkMenu mount effect): un-paints ONLY
    // (clearLinkTargetLayer) — the capture itself DELIBERATELY SURVIVES,
    // inert, until either the next beginLinkTarget() overwrites it or a
    // genuine Apply/Remove consumes it. This asymmetry is load-bearing, not
    // an oversight: React StrictMode (dev only) double-invokes a fresh
    // effect's mount -> cleanup -> mount in sequence, so NoteLinkMenu's own
    // mount effect's cleanup runs this null branch immediately after its
    // FIRST (phantom) mount, before the SECOND (real) mount's effect calls
    // repaintLinkTarget() to restore the paint — if this branch had dropped
    // the capture too, that second mount would have nothing left to
    // repaint from, and the highlight (and, since resolve/removeLinkTarget
    // both no-op with nothing captured, Apply/Remove too) would silently
    // stop working the instant StrictMode was in play. No execCommand and
    // no stolen focus either way for url === null — there is nothing to
    // apply, so nothing about the editor's own focus/selection should
    // change just because the submenu closed (or StrictMode blinked).
    // Idempotent regardless: a no-capture call (already resolved, or
    // beginLinkTarget never succeeded) is always a safe no-op, which is
    // what lets the cleanup effect call this unconditionally on every close
    // path (including the phantom one).
    const resolveLinkTarget = (url) => {
        if (url == null) {
            clearLinkTargetLayer();
            return;
        }
        // A collapsed capture (caret in plain text — see beginLinkTarget's
        // three shapes) means there is no text to wrap: insert a NEW link at
        // the restored caret with the url as its text instead.
        const collapsed = !!linkTargetRef.current?.snapshot?.collapsed;
        restoreLinkTargetThen(() => (collapsed ? insertNewLinkAtCaret(url) : execLink(url)));
        clearLinkTarget();
    };

    // Remove-link counterpart: restores the captured selection (same shared
    // shape as resolveLinkTarget(url) above) and runs the existing removeLink
    // body over it, then un-paints. ContextMenu's ALWAYS-run cleanup effect
    // still calls resolveLinkTarget(null) after this on submenu close, but
    // by then nothing is captured, so it's the safe no-op described above.
    const removeLinkTarget = () => {
        restoreLinkTargetThen(removeLink);
        clearLinkTarget();
    };

    // In-editor link click popover's Edit action (spec 8b step 3): capture
    // the clicked link's WHOLE extent (beginLinkTargetForAnchor — paints the
    // highlight strips over it, same as the toolbar Link button's own
    // capture), then open the existing NoteLinkMenu EXACTLY the way that
    // toolbar button does — same noteLinkMenuArgsRef shape (editor/canLink/
    // prefillHref/anchorRect), just handed to ContextMenu.jsx over
    // noteLinkEditRequestBridge instead of written in-place, since
    // noteLinkMenuArgsRef/handleMenuClick are that OTHER component's own
    // state (see the bridge module's own doc comment for why). `canLink` is
    // unconditionally true here — unlike the toolbar button (which computes
    // it from whether a live selection existed to capture),
    // beginLinkTargetForAnchor already only reaches this point on a
    // successful capture.
    //
    // Close order matters: this popover only closes once NoteLinkMenu has
    // actually taken over. `requestNoteLinkEdit` returns whether a listener
    // was there to consume the request (ContextMenu should always be
    // mounted alongside an open editor, but "should always" isn't a
    // guarantee) — on a false return, closing this popover AND its
    // highlight would leave the user with no visible outcome at all
    // (silently vanishing); instead, everything captured/painted by
    // beginLinkTargetForAnchor is left exactly as-is and this popover stays
    // open. A failed CAPTURE (stale/detached anchorEl) is the one case that
    // still closes unconditionally — nothing was painted, so there is
    // nothing worth keeping open for.
    const handleLinkClickEdit = () => {
        const popover = linkClickPopover;
        if (!popover) return;
        const captured = beginLinkTargetForAnchor(popover.anchorEl);
        if (!captured) {
            closeLinkClickPopover();
            return;
        }
        const consumed = requestNoteLinkEdit({
            noteId,
            canLink: true,
            prefillHref: popover.href,
            anchorRect: getLinkTargetRect(),
        });
        if (!consumed) return;
        closeLinkClickPopover();
    };

    // In-editor link click popover's Remove action (spec 8b step 4): capture
    // THIS link's extent, then run the existing remove machinery over it
    // (removeLinkTarget — restores the captured selection, execCommand
    // 'unlink', canonicalizes, un-paints) — strips only this link, text and
    // other marks untouched, same as every other removeLinkTarget caller.
    const handleLinkClickRemove = () => {
        const popover = linkClickPopover;
        if (!popover) return;
        if (beginLinkTargetForAnchor(popover.anchorEl)) removeLinkTarget();
        closeLinkClickPopover();
    };

    // Collapsed caret: marks surrounding that exact DOM position (the
    // "typing state"), with any armed pending toggles applied on top (spec
    // rule 5.D — menu indicators reflect the EFFECTIVE typing style, marks
    // at caret XOR pending, not just the ambient marks). Non-collapsed: a
    // mark is reported true only if every intersected text node carries it
    // (mirrors toggleStyleAll/toggleListAll's all-or-nothing semantics, so a
    // toolbar button's active state matches what clicking it again would
    // undo); linkHref is the first href seen regardless of whether the whole
    // selection is one link.
    const getSelectionStyles = () => {
        const empty = { bold: false, italic: false, underline: false, strike: false, link: false, linkHref: null };
        const el = editorRef.current;
        const range = currentRange();
        if (!el || !range) return empty;

        if (range.collapsed) {
            const baseline = marksAtNode(range.startContainer, el);
            if (!isPendingStyleArmed(pendingRef.current)) return baseline;
            return { ...baseline, ...effectiveStyleMarks(baseline, pendingRef.current.pending) };
        }

        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => (node.nodeValue && range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
        });
        const acc = { bold: true, italic: true, underline: true, strike: true, link: true };
        let linkHref = null;
        let count = 0;
        let node = walker.nextNode();
        while (node) {
            count += 1;
            const marks = marksAtNode(node, el);
            MARK_LIST.forEach((key) => { if (!marks[key]) acc[key] = false; });
            if (marks.link && linkHref === null) linkHref = marks.linkHref;
            node = walker.nextNode();
        }
        if (!count) return empty;
        return { ...acc, linkHref };
    };

    // Only teleports the caret to the end when the DOM selection isn't
    // already inside this editor (e.g. focus() called right after mount, or
    // from outside). When a menu action calls this to return focus after an
    // execCommand, the selection it just restored via restoreCaretPosition
    // is already inside root — jumping to placeCaretAtEnd here would discard
    // it on every single menu click.
    const focusEditor = () => {
        const el = editorRef.current;
        if (!el) return;
        if (currentRange()) {
            el.focus();
            return;
        }
        el.focus();
        placeCaretAtEnd(el);
    };

    useEffect(() => {
        const el = editorRef.current;
        if (el) {
            el.innerHTML = editorHtmlFromText(text);
            el.focus();
            // initialCaretPoint only arrives from the opening-click path
            // (Canvas.jsx's noteOpenClickPointRef) — every other way to open
            // this editor (new-note creation, a future search commit, the
            // bridge's focus()) passes none, and lands at end-of-text exactly
            // as before.
            if (initialCaretPoint) {
                placeCaretAtPoint(el, initialCaretPoint.x, initialCaretPoint.y);
            } else {
                placeCaretAtEnd(el);
            }
            // Consume-and-clear: tell Canvas its stashed point was just used
            // so it can null noteOpenClickPointRef right now, exactly once.
            // Without this, a REMOUNT of this same note's editor while
            // focusedInsideElementState never left 'note-text' — e.g. a
            // wheel-zoom's transient isScaling gate unmounting then
            // remounting this component — would replay the ORIGINAL click's
            // point on the new mount instead of landing at end-of-text.
            onCaretPointConsumed?.();
        }
        autosize();
        // Runs once at mount only — the editor is uncontrolled thereafter,
        // and intentionally not re-run when `text` or `initialCaretPoint`
        // change after mount.
    }, []);

    // apiRef always holds this render's freshest closures; the object hand-
    // ed to the bridge is a stable set of thin delegators created once per
    // noteId, so Task 6's context menu can hold a long-lived reference to it
    // without this effect re-registering on every keystroke/re-render.
    apiRef.current = {
        hasSelection, execStyle, execList, execLink, removeLink,
        getSelectionSnapshot, applyLinkAt, removeLinkAt,
        beginLinkTarget, getLinkTargetRect, repaintLinkTarget, resolveLinkTarget, removeLinkTarget,
        getSelectionStyles, focus: focusEditor,
    };

    useEffect(() => {
        registerNoteEditor({
            noteId,
            hasSelection: (...args) => apiRef.current.hasSelection(...args),
            execStyle: (...args) => apiRef.current.execStyle(...args),
            execList: (...args) => apiRef.current.execList(...args),
            execLink: (...args) => apiRef.current.execLink(...args),
            removeLink: (...args) => apiRef.current.removeLink(...args),
            getSelectionSnapshot: (...args) => apiRef.current.getSelectionSnapshot(...args),
            applyLinkAt: (...args) => apiRef.current.applyLinkAt(...args),
            removeLinkAt: (...args) => apiRef.current.removeLinkAt(...args),
            beginLinkTarget: (...args) => apiRef.current.beginLinkTarget(...args),
            getLinkTargetRect: (...args) => apiRef.current.getLinkTargetRect(...args),
            repaintLinkTarget: (...args) => apiRef.current.repaintLinkTarget(...args),
            resolveLinkTarget: (...args) => apiRef.current.resolveLinkTarget(...args),
            removeLinkTarget: (...args) => apiRef.current.removeLinkTarget(...args),
            getSelectionStyles: (...args) => apiRef.current.getSelectionStyles(...args),
            focus: (...args) => apiRef.current.focus(...args),
        });
        return () => unregisterNoteEditor(noteId);
        // Intentionally keyed on noteId alone — apiRef.current always holds
        // this render's freshest closures, so re-registering on every
        // render/keystroke is unnecessary (see apiRef assignment above).
    }, [noteId]);

    // Spec rule 5.C's catch-all break rule: any caret movement or new
    // selection NOT caused by our own typing/backspace handling disarms
    // pending — mouse clicks, arrow keys, programmatic moves from elsewhere
    // all funnel through the browser's selectionchange event, so one
    // document-level listener covers all of them without hooking every
    // possible cause individually. Scoped to this editor (ignores selection-
    // changes elsewhere on the page) and gated by the self-move latch (see
    // selfMoveRef's declaration) so our OWN caret repositioning — from
    // insertStyledText while typing, or the native delete a Backspace is
    // allowed to proceed with while pending stays armed — doesn't immediately
    // undo the very state transition that caused it.
    //
    // Manual-QA / inspection-only wiring: jsdom does not model a realistic
    // native typing pipeline (no execCommand, no OS-level beforeinput for
    // simulated keystrokes), so this listener is not exercised by the
    // automated suite — see pendingStyleReducer's own tests for the state
    // transitions this drives.
    useEffect(() => {
        const handleSelectionChange = () => {
            if (selfMoveRef.current) {
                selfMoveRef.current = false;
                return;
            }
            if (!isPendingStyleArmed(pendingRef.current)) return;
            const el = editorRef.current;
            if (!el) return;
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;
            const range = selection.getRangeAt(0);
            if (!el.contains(range.commonAncestorContainer)) return;
            // Routed through the reducer's own caretMoved/selectionMade
            // events (rather than a bare state reset) — both already exist
            // in pendingStyleReducer specifically for this distinction, so
            // this is the single source of truth for what disarms pending,
            // same as the 'selectionMade' routing above.
            pendingRef.current = pendingStyleReducer(pendingRef.current, {
                type: range.collapsed ? 'caretMoved' : 'selectionMade',
            });
        };
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    const handleInput = () => {
        syncFromDom();
    };

    // Spec 8b, editor case: a click anywhere in the editor is left to
    // proceed NORMALLY (no preventDefault — the browser's own native
    // contenteditable behavior places the caret at the click point; a
    // contenteditable's own <a> does not navigate on a plain click, by
    // design, so nothing here needs to suppress that either). Additionally,
    // walk the click target up to its nearest <a href> ancestor (bounded at
    // this editor's root): a hit opens/retargets the click popover over
    // THAT link; a miss (clicked elsewhere in the editor's own text) closes
    // whatever was open — spec's "click elsewhere in the editor" close path.
    // "Click elsewhere OUTSIDE the editor" and "typing" are separate close
    // paths (handleEditorBlur below, and syncFromDom's own close call).
    const handleEditorClick = (event) => {
        const el = editorRef.current;
        if (!el) return;
        const anchorEl = findLinkAncestor(event.target, el);
        if (!anchorEl) {
            closeLinkClickPopover();
            return;
        }
        setLinkClickPopover({ href: anchorEl.getAttribute('href') || '', anchorEl });
    };

    // Spec 8b: "editor unmount/blur to non-popover targets" closes the
    // popover. The popover's own Edit/Remove buttons preventDefault their
    // mousedown (see the JSX below, same pattern as the note toolbar's own
    // Link button — search this file's own render for "steal focus") so
    // clicking them never blurs the editor in the first place; this handler
    // only ever fires for a GENUINE focus loss (the user clicked/tabbed
    // somewhere else entirely, or another note/action took focus), so it can
    // unconditionally close without needing to special-case the popover's
    // own controls.
    const handleEditorBlur = () => {
        closeLinkClickPopover();
    };

    // Prefers the browser's own beforeinput target range — exact, reflects
    // whatever the browser actually resolved to delete, including full
    // grapheme clusters beyond a bare surrogate pair — over the pure
    // DOM/selection heuristic (backspaceDeleteCount) that jsdom (no
    // getTargetRanges support) and any other environment without it falls
    // back to. A collapsed target range (nothing to delete) naturally
    // yields 0 through this path too, same as backspaceDeleteCount's own
    // doc-start case.
    const resolveBackspaceCount = (event, rootEl, selection) => {
        const targetRanges = typeof event.nativeEvent?.getTargetRanges === 'function' ? event.nativeEvent.getTargetRanges() : null;
        if (targetRanges && targetRanges[0]) {
            const staticRange = targetRanges[0];
            const probe = document.createRange();
            probe.setStart(staticRange.startContainer, staticRange.startOffset);
            probe.setEnd(staticRange.endContainer, staticRange.endOffset);
            return probe.toString().length;
        }
        return backspaceDeleteCount(rootEl, selection);
    };

    // Spec rule 5.B: while a pending typing style is armed, plain-text
    // insertion is intercepted so the typed characters can be wrapped in the
    // effective marks (marks at caret XOR pending) as canonical tags instead
    // of landing with whatever the browser's own native formatting would
    // have produced. When pending is NOT armed this is a no-op — typing
    // stays fully native, same as before this feature (protects native
    // undo/redo for the common case).
    //
    // Also handles 'deleteContentBackward' (spec rule 5.C's ordinary,
    // non-list-start Backspace — the list-start/unwrap case is a distinct
    // structural edit handled entirely in keydown, see handleKeyDown). The
    // delete itself is always left native here (no preventDefault): this
    // branch only derives how many UTF-16 units are about to be removed
    // (Issue: a flat "1 per Backspace" undercounts an emoji/astral
    // character, which is a surrogate PAIR removed atomically) and feeds
    // that into the reducer's 'backspace' event so typedCount accounting
    // stays exact. A count of 0 (nothing will actually be deleted — caret
    // at the very start of the note) is a true no-op: no reducer dispatch,
    // and — importantly — no self-move flag either, since no selectionchange
    // will follow to consume it (a stale flag would otherwise swallow the
    // NEXT real caret-move disarm).
    const handleBeforeInput = (event) => {
        if (event.nativeEvent?.isComposing) return;
        if (!isPendingStyleArmed(pendingRef.current)) return;

        const el = editorRef.current;
        if (!el) return;

        if (event.inputType === 'deleteContentBackward') {
            const count = resolveBackspaceCount(event, el, window.getSelection());
            if (count === 0) return;
            selfMoveRef.current = true;
            pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'backspace', count });
            return;
        }

        if (event.inputType !== 'insertText' || !event.data) return;

        const range = currentRange();
        if (!range || !range.collapsed) return;

        event.preventDefault();

        const baseline = marksAtNode(range.startContainer, el);
        const effective = effectiveStyleMarks(baseline, pendingRef.current.pending);
        const marks = {};
        STYLE_MARK_KEYS.forEach((key) => { if (effective[key]) marks[key] = true; });

        selfMoveRef.current = true;
        insertStyledText(el, window.getSelection(), event.data, marks);
        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'typed', count: event.data.length });
        syncFromDom();
    };

    const handleKeyDown = (event) => {
        // IME composition (CJK/etc. input methods) sends Enter/Backspace-
        // shaped keydowns to confirm/cancel the candidate window, not to
        // edit the note — intercepting them here would corrupt the
        // in-progress composition. keyCode 229 is the legacy signal some
        // browsers still send instead of (or in addition to) isComposing.
        if (event.nativeEvent?.isComposing || event.keyCode === 229) return;

        const el = editorRef.current;
        if (!el) return;
        const selection = window.getSelection();

        // Spec 8b: Escape closes the in-editor link click popover. Closes
        // ONLY the popover, not preventDefault/stopPropagation — some other
        // (pre-existing, elsewhere-owned) Escape handling may want this same
        // keydown too (e.g. exiting note-edit mode entirely), and this
        // popover closing is purely additive on top of that, never a
        // replacement for it.
        if (event.key === 'Escape' && linkClickPopover) {
            closeLinkClickPopover();
            return;
        }

        if (event.key === 'Enter') {
            if (handleEnter(el, selection)) {
                event.preventDefault();
                // Spec rule 5.C: Enter always disarms — the new row starts
                // clean (rule 6), so there is no "pending style" left for it
                // to carry.
                pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'enter' });
                syncFromDom();
            }
            return;
        }

        if (event.key === 'Backspace') {
            // handleBackspaceAtListStart only returns true when it actually
            // unwraps something, and that unwrap always repositions the
            // caret — so the flag only needs to be set AFTER we know that,
            // not defensively before calling it. This is still race-free
            // despite being "after the move already happened": selectionchange
            // fires as a queued task (never mid-synchronous-call), so it
            // cannot run until this whole handler has returned regardless of
            // exactly which line set the flag.
            if (handleBackspaceAtListStart(el, selection)) {
                event.preventDefault();
                selfMoveRef.current = true;
                // Structural edit, not a typing continuation — per review,
                // routed through the reducer's own 'backspaceStructural'
                // event rather than an unconditional reset: typedCount === 0
                // (nothing typed under pending yet) keeps it armed exactly
                // like a no-op character Backspace would; typedCount > 0
                // disarms, same as Enter/execList.
                pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'backspaceStructural' });
                syncFromDom();
                return;
            }

            // Not a list-unwrap: an ordinary character delete, left fully
            // native here (no preventDefault). While pending is armed, the
            // actual typedCount accounting — and the self-move flag guarding
            // it — happens in handleBeforeInput's 'deleteContentBackward'
            // branch instead of here, because THAT handler can see whether
            // the browser is actually about to delete anything before
            // deciding whether to flag/dispatch at all. Flagging unconditionally
            // here (before knowing that) would arm the latch even for a
            // caret-at-document-start Backspace that deletes nothing and
            // produces no selectionchange to consume it — silently
            // swallowing the NEXT real one.
            return;
        }

        if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey) {
            const key = event.key.toLowerCase();
            if (key === 'b') { event.preventDefault(); execStyle('bold'); return; }
            if (key === 'i') { event.preventDefault(); execStyle('italic'); return; }
            if (key === 'u') { event.preventDefault(); execStyle('underline'); }
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const el = editorRef.current;
        if (!el) return;
        // Pasted content carries its own (plain-text) shape, not the pending
        // typing style — disarm rather than leaving stale marks armed for
        // whatever gets typed after the paste. Not one of the spec's
        // literally-enumerated break rules, but the user signed off on
        // paste-disarms as the accepted behavior. Routed through the
        // reducer so it stays the single source of truth.
        pendingRef.current = pendingStyleReducer(pendingRef.current, { type: 'selectionMade' });
        handlePasteText(el, window.getSelection(), event.clipboardData.getData('text/plain'));
        syncFromDom();
    };

    const fontSize = isFontMonospace ? NOTE_FONT_MONO_SIZE : NOTE_FONT_SIZE;

    // CSS line-boxes add leading above the first line (from the font's
    // ascent/descent metrics) that drawNote's `padding + fontSize` baseline
    // math doesn't have. Absolute world-px compensation, visually tuned per
    // font family (each has different metrics): decrease if the first line
    // sits high vs the canvas text, increase if low.
    const topLeading = isFontMonospace ? 2 : 1.3;

    const wrapperStyles = {
        position: 'absolute',
        left: (noteX + NOTE_EDITOR_INSET) * scale + originXY.x,
        top: (noteY + NOTE_EDITOR_INSET) * scale + originXY.y,
        width: (noteWidth - 2 * NOTE_EDITOR_INSET) * scale,
        height: (noteHeight - 2 * NOTE_EDITOR_INSET - NOTE_EDITOR_BOTTOM_REVEAL) * scale,
        background: noteBodyColor(color),
        borderRadius: Math.max(NOTE_BORDER_RADIUS - NOTE_EDITOR_INSET, 0) * scale,
        // vertical paddings live on the wrapper (not the editor): the editor
        // scrolls text through its own padding, so when content overflows and
        // the caret walks up/down, editor-side padding visually vanishes.
        // On the wrapper, the editor's scroll viewport IS the padded content
        // box — both edges keep their breathing room while scrolling.
        paddingTop: (NOTE_PADDING_Y - topLeading - NOTE_EDITOR_INSET) * scale,
        paddingBottom: Math.max(NOTE_PADDING_Y - NOTE_EDITOR_INSET - NOTE_EDITOR_BOTTOM_REVEAL, 0) * scale,
        display: 'flex',
        alignItems: alignmentVertical === 'middle' ? 'center' : alignmentVertical === 'bottom' ? 'flex-end' : 'flex-start',
    };

    const editorStyles = {
        // vertical paddings are 0 here — the wrapper carries them (see
        // wrapperStyles); only the horizontal padding stays on the editor
        padding: `0 ${(NOTE_PADDING_X - NOTE_EDITOR_INSET) * scale}px 0`,
        fontSize: fontSize * scale,
        lineHeight: `${NOTE_LINE_HEIGHT * scale}px`,
        fontFamily: isFontMonospace ? "'Roboto Mono', sans-serif" : "'Inter', sans-serif",
        textAlign: alignmentHorizontal || 'left',
        // List geometry, in the SAME world constants drawNote paints with
        // (richTextLayout.js) — canvas gets its zoom scaling for free from
        // the 2D context's own transform, but this DOM overlay has no such
        // transform (see wrapperStyles/this object's other properties, all
        // pre-scaled by `scale` by hand), so these three custom properties
        // are the list rules' own by-hand equivalent. Set here (not on the
        // wrapper) so they inherit down to the ul/ol/li rules in
        // EditableNote.scss, which is the only place that reads them.
        '--note-list-indent': `${LIST_INDENT * scale}px`,
        '--note-dot-x': `${BULLET_DOT_X * scale}px`,
        '--note-dot-r': `${BULLET_DOT_RADIUS * scale}px`,
    };

    // Same radius as the wrapper (wrapperStyles.borderRadius) so a highlight
    // strip painted right at the note's rounded corner clips to the same
    // shape instead of the layer's own plain rectangle — everything else
    // about this element's own box (position/size) just needs to be a
    // STABLE, measurable reference frame for paintLinkTargetLayer's rect
    // subtraction, not a pixel-perfect match to the editor's own padding.
    const highlightLayerStyles = {
        // borderRadius: wrapperStyles.borderRadius,
    };

    // In-editor link click popover (spec 8b): portaled to document.body,
    // same reasoning as NoteLinkMenu (ContextMenu.jsx) — `position: fixed`
    // (NoteLinkPreview's own positioning) only reliably escapes to the
    // viewport with nothing in the ancestor chain deciding a new containing
    // block (a CSS transform/filter/perspective anywhere between here and
    // <body>), and nothing in this file's own render tree can guarantee
    // that holds all the way up through Canvas.jsx's own root. Rect is
    // read fresh from the live anchorEl on every render (not stored once at
    // click time — see linkClickPopover's own declaration for why), guarded
    // by an editorRef.contains check so a detached/foreign anchorEl (should
    // not be reachable — every edit closes this popover via syncFromDom —
    // but defensive regardless) renders nothing instead of a garbage rect.
    const linkClickPopoverRect = (linkClickPopover && editorRef.current && editorRef.current.contains(linkClickPopover.anchorEl))
        ? linkClickPopover.anchorEl.getBoundingClientRect()
        : null;

    return (
        <>
            <div className="editable-note" style={wrapperStyles}>
                {/* Highlight layer: a SIBLING of the contenteditable, rendered
                    BEFORE it in DOM order (see paintLinkTargetLayer's own doc
                    comment for why that alone is enough for the stacking this
                    needs, no z-index). No `children` here — its content is
                    owned entirely by paintLinkTargetLayer/clearLinkTargetLayer,
                    the exact same "React never diffs what it doesn't render"
                    shape editorRef's own element already relies on below. */}
                <div ref={highlightLayerRef} className="editable-note-highlight-layer" style={highlightLayerStyles} />
                <div
                    ref={editorRef}
                    className="editable-note-editor"
                    contentEditable
                    suppressContentEditableWarning
                    spellCheck={false}
                    style={editorStyles}
                    onInput={handleInput}
                    onBeforeInput={handleBeforeInput}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onClick={handleEditorClick}
                    onBlur={handleEditorBlur}
                />
            </div>
            {linkClickPopoverRect && createPortal(
                <NoteLinkPreview
                    href={linkClickPopover.href}
                    position={{ x: linkClickPopoverRect.left, y: linkClickPopoverRect.bottom }}
                    actions={(
                        <>
                            {/* Mousedown preventDefault on both buttons — same
                                pattern as the note toolbar's own Link button
                                (ContextMenu.jsx, "must not steal focus from the
                                note's contenteditable"): without this, clicking
                                either button would blur the editor (dropping the
                                caret) before its onClick ever got to capture
                                anything.

                                Icon-only (no visible text label) — the
                                accessible name comes from `aria-label` instead,
                                off the SAME i18n keys the text label used to
                                render from directly (see the commented-out
                                `t(...)` calls below each icon: that's what
                                `aria-label`/`title` now read, so the
                                translation is still the single source of
                                truth for what these buttons are called, just
                                surfaced to assistive tech / a hover tooltip
                                instead of on-screen text). `title` mirrors it
                                for a native mouse-hover tooltip, same
                                reasoning as NoteLinkPreview's own `title={href}`
                                on the URL row. */}
                            <button
                                type="button"
                                className="note-link-preview__action-btn"
                                // aria-label={t('t.ctx.note.link_edit')}
                                // title={t('t.ctx.note.link_edit')}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={handleLinkClickEdit}
                            >
                                <PenEditIcon />

                            </button>
                            <button
                                type="button"
                                className="note-link-preview__action-btn"
                                // aria-label={t('t.ctx.note.link_remove')}
                                // title={t('t.ctx.note.link_remove')}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={handleLinkClickRemove}
                            >
                                <DeleteIcon />
                            </button>
                        </>
                    )}
                />,
                document.body
            )}
        </>
    );
};

export default EditableNote;
